import React, { useState, useRef, useEffect } from "react";
import {
  MdAssessment,
  MdSearch,
  MdAccessTime,
  MdCalendarToday,
  MdBarChart,
  MdBuild,
  MdLocationOn,
  MdImage,
  MdDescription,
  MdPictureAsPdf,
} from "react-icons/md";
import DatePicker, { registerLocale } from "react-datepicker";
// eslint-disable-next-line import/no-duplicates
import ptBR from "date-fns/locale/pt-BR";
// eslint-disable-next-line import/no-duplicates
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { PageTitle } from "../../components/PageTitle";
import { Loading } from "../../components/Loading";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { api } from "../../services/api";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ptBR", ptBR);

interface RankingItem {
  id: string;
  name: string;
  count: number;
}

interface CrossTableRow {
  sector_id: string;
  sector_name: string;
  [equipment_id: string]: string | number;
}

interface EquipmentDetail {
  id: string;
  name: string;
  grade: string;
}

interface SectorDetail {
  sector_id: string;
  sector_name: string;
  observation: string;
  images: Array<{ id: string; file_name: string }>;
  equipments: EquipmentDetail[];
}

interface ReportDetail {
  report_id: string;
  report_code: number;
  report_date: string;
  sectors: SectorDetail[];
}

interface ManagerialReport {
  costumer: {
    id: string;
    name: string;
    address: string;
  };
  totalReports: number;
  averageVisitTimeMinutes: number;
  averageDaysBetweenReports: number;
  topSectors: RankingItem[];
  topEquipments: RankingItem[];
  crossTable: {
    equipments: Array<{ id: string; name: string }>;
    rows: CrossTableRow[];
  };
}

interface CustomerOption {
  id: string;
  name: string;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function MedalBadge({ position }: { position: number }) {
  const colors = ["bg-yellow-400", "bg-gray-300", "bg-amber-600"];
  const labels = ["1º", "2º", "3º"];
  return (
    <span
      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${colors[position]}`}
    >
      {labels[position]}
    </span>
  );
}

export function ManagerialReport() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [begin, end] = dateRange;
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [report, setReport] = useState<ManagerialReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSectorIds, setSelectedSectorIds] = useState<string[]>([]);
  const [detailedView, setDetailedView] = useState<ReportDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const hasLoadedInitialCustomers = useRef(false);

  // Fecha o dropdown quando clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  async function loadInitialCustomers() {
    if (hasLoadedInitialCustomers.current) return;

    setLoadingCustomers(true);
    try {
      const response = await api.get("/costumers/search?name=");
      setCustomerOptions(response.data);
      hasLoadedInitialCustomers.current = true;
    } catch {
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  }

  async function searchCustomers(term: string) {
    setLoadingCustomers(true);
    try {
      const response = await api.get(`/costumers/search?name=${term}`);
      setCustomerOptions(response.data);
      setShowDropdown(true);
    } catch {
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  }

  function handleCustomerInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setCustomerSearch(val);
    setSelectedCustomer(null);
    if (val.length >= 1) {
      searchCustomers(val);
    } else {
      // Se apagar tudo, mostra a lista inicial novamente
      loadInitialCustomers();
      setShowDropdown(true);
    }
  }

  function handleSelectCustomer(customer: CustomerOption) {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowDropdown(false);
  }

  async function handleGenerate() {
    if (!selectedCustomer) {
      enqueueSnackbar("Selecione um cliente.", { variant: "warning" });
      return;
    }
    if (!begin || !end) {
      enqueueSnackbar("Informe o período completo.", { variant: "warning" });
      return;
    }
    setError("");
    setLoading(true);
    try {
      const response = await api.get("/techVisitReport/managerial-report", {
        params: {
          costumer_id: selectedCustomer.id,
          begin: begin.toISOString(),
          end: end.toISOString(),
        },
      });
      setReport(response.data);
      enqueueSnackbar("Relatório gerado com sucesso!", { variant: "success" });
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || "Erro ao gerar relatório.";
      enqueueSnackbar(errorMsg, { variant: "error" });
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoadDetailedView() {
    if (!selectedCustomer || !begin || !end) {
      return;
    }
    if (selectedSectorIds.length === 0) {
      enqueueSnackbar("Selecione pelo menos um setor para visualizar os detalhes.", {
        variant: "warning",
      });
      return;
    }
    setError("");
    setLoadingDetails(true);
    try {
      const response = await api.get("/techVisitReport/detailed-view", {
        params: {
          costumer_id: selectedCustomer.id,
          begin: begin.toISOString(),
          end: end.toISOString(),
          sector_ids: selectedSectorIds.join(","),
        },
      });
      setDetailedView(response.data);
      enqueueSnackbar("Visão detalhada carregada!", { variant: "success" });
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message || "Erro ao carregar visão detalhada.";
      enqueueSnackbar(errorMsg, { variant: "error" });
      setDetailedView([]);
    } finally {
      setLoadingDetails(false);
    }
  }

  function getGradeLabel(grade: string): string {
    const labels: Record<string, string> = {
      O: "Ótimo",
      B: "Bom",
      R: "Revisado",
      S: "Substituído",
    };
    return labels[grade] || grade;
  }

  function getGradeColor(grade: string): string {
    const colors: Record<string, string> = {
      O: "bg-green-100 text-green-700",
      B: "bg-blue-100 text-blue-700",
      R: "bg-yellow-100 text-yellow-700",
      S: "bg-red-100 text-red-700",
    };
    return colors[grade] || "bg-gray-100 text-gray-700";
  }

  async function handleExportPdf() {
    if (!selectedCustomer || !begin || !end) {
      enqueueSnackbar("Selecione os filtros antes de exportar.", {
        variant: "warning",
      });
      return;
    }

    setLoadingPdf(true);
    enqueueSnackbar("Gerando PDF... Isso pode levar alguns minutos.", {
      variant: "info",
    });

    try {
      const params: any = {
        costumer_id: selectedCustomer.id,
        begin: begin.toISOString(),
        end: end.toISOString(),
      };

      // Inclui sector_ids se houver visão detalhada carregada
      if (selectedSectorIds.length > 0) {
        params.sector_ids = selectedSectorIds.join(",");
      }

      const response = await api.get("/techVisitReport/managerial-report/pdf", {
        params,
        responseType: "blob",
        timeout: 300000, // 5 minutos de timeout para geração de PDF
      });

      // Cria um link temporário para download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio-gerencial-${selectedCustomer.name}-${format(
        new Date(),
        "yyyy-MM-dd"
      )}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("PDF gerado com sucesso!", { variant: "success" });
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || "Erro ao gerar PDF.";
      enqueueSnackbar(errorMsg, { variant: "error" });
    } finally {
      setLoadingPdf(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Loading isLoading={loading} />

      <PageTitle
        title="Relatório Gerencial"
        subtitle="Compilado de visitas técnicas por cliente"
      />

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Filtros
        </h3>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          {/* Cliente */}
          <div className="flex-1 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cliente
            </label>
            <div className="relative">
              <input
                type="text"
                value={customerSearch}
                onChange={handleCustomerInputChange}
                onFocus={() => {
                  if (customerOptions.length > 0) {
                    setShowDropdown(true);
                  } else {
                    loadInitialCustomers();
                    setShowDropdown(true);
                  }
                }}
                placeholder="Digite para buscar..."
                className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] focus:outline-none"
              />
              {loadingCustomers && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#0085BD] border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            {showDropdown && customerOptions.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto"
              >
                {customerOptions.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-4 py-2.5 hover:bg-[#0085BD]/10 text-sm text-gray-700"
                    onMouseDown={() => handleSelectCustomer(c)}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Período */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Período
            </label>
            <DatePicker
              selectsRange
              startDate={begin}
              endDate={end}
              onChange={(update) => {
                setDateRange(update as [Date | null, Date | null]);
              }}
              dateFormat="dd/MM/yyyy"
              locale="ptBR"
              placeholderText="Selecione o período"
              className="h-[52px] px-4 w-80 rounded-lg text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] focus:outline-none"
              isClearable
            />
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            className="h-[52px] px-6 rounded-xl bg-[#0085BD] text-white font-semibold flex items-center gap-2 hover:bg-[#006a96] transition-colors shadow-sm"
          >
            <MdSearch size={20} />
            Gerar
          </button>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-500 font-medium">{error}</p>
        )}
      </div>

      {/* Resultado */}
      {report && (
        <div className="space-y-6">
          {/* Cabeçalho do cliente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0085BD]/10 flex items-center justify-center flex-shrink-0">
                  <MdLocationOn size={24} className="text-[#0085BD]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {report.costumer.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {report.costumer.address}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Período:{" "}
                    <span className="font-medium text-gray-600">
                      {begin && format(begin, "dd/MM/yyyy", { locale: ptBR })} –{" "}
                      {end && format(end, "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={loadingPdf}
                className="h-11 px-5 rounded-xl bg-red-600 text-white font-semibold flex items-center gap-2 hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title={loadingPdf ? "Isso pode levar até 3 minutos, aguarde..." : "Exportar relatório em PDF"}
              >
                {loadingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Gerando (pode levar até 3min)...
                  </>
                ) : (
                  <>
                    <MdPictureAsPdf size={20} />
                    Exportar PDF
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Métricas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard
              icon={<MdAssessment size={24} className="text-[#0085BD]" />}
              label="Relatórios no período"
              value={String(report.totalReports)}
              bg="bg-[#0085BD]/10"
            />
            <MetricCard
              icon={<MdAccessTime size={24} className="text-emerald-600" />}
              label="Tempo médio de visita"
              value={formatMinutes(report.averageVisitTimeMinutes)}
              bg="bg-emerald-50"
            />
            <MetricCard
              icon={<MdCalendarToday size={24} className="text-purple-600" />}
              label="Média de dias entre visitas"
              value={
                report.averageDaysBetweenReports > 0
                  ? `${report.averageDaysBetweenReports} dias`
                  : "—"
              }
              bg="bg-purple-50"
            />
          </div>

          {/* Rankings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Setores */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MdBarChart size={20} className="text-[#0085BD]" />
                <h3 className="font-semibold text-gray-700">
                  Top 3 Setores — Equipamentos Revisados
                </h3>
              </div>
              {report.topSectors.length === 0 ? (
                <p className="text-sm text-gray-400">
                  Nenhum equipamento revisado no período.
                </p>
              ) : (
                <ol className="space-y-3">
                  {report.topSectors.map((item, idx) => (
                    <li key={item.id} className="flex items-center gap-3">
                      <MedalBadge position={idx} />
                      <span className="flex-1 text-sm text-gray-700 font-medium">
                        {item.name}
                      </span>
                      <span className="text-sm font-bold text-[#0085BD]">
                        {item.count}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Top Equipamentos */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MdBuild size={20} className="text-[#0085BD]" />
                <h3 className="font-semibold text-gray-700">
                  Top 3 Equipamentos — Mais Revisados
                </h3>
              </div>
              {report.topEquipments.length === 0 ? (
                <p className="text-sm text-gray-400">
                  Nenhum equipamento revisado no período.
                </p>
              ) : (
                <ol className="space-y-3">
                  {report.topEquipments.map((item, idx) => (
                    <li key={item.id} className="flex items-center gap-3">
                      <MedalBadge position={idx} />
                      <span className="flex-1 text-sm text-gray-700 font-medium">
                        {item.name}
                      </span>
                      <span className="text-sm font-bold text-[#0085BD]">
                        {item.count}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Tabela cruzada */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-700 mb-4">
              Equipamentos Revisados por Setor
            </h3>
            {report.crossTable.rows.length === 0 ||
            report.crossTable.equipments.length === 0 ? (
              <p className="text-sm text-gray-400">
                Nenhum dado disponível para a tabela.
              </p>
            ) : (
              <div
                ref={tableRef}
                className="overflow-x-auto rounded-xl border border-gray-100"
              >
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      {/* Coluna setor — sticky */}
                      <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap min-w-[180px]">
                        Setor
                      </th>
                      {report.crossTable.equipments.map((eq) => (
                        <th
                          key={eq.id}
                          className="px-4 py-3 text-center font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap"
                        >
                          {eq.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.crossTable.rows.map((row, rowIdx) => (
                      <tr
                        key={row.sector_id}
                        className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
                      >
                        <td className="sticky left-0 z-10 px-4 py-3 font-medium text-gray-700 border-b border-gray-100 whitespace-nowrap bg-inherit">
                          {row.sector_name}
                        </td>
                        {report.crossTable.equipments.map((eq) => {
                          const val = row[eq.id] as number;
                          return (
                            <td
                              key={eq.id}
                              className="px-4 py-3 text-center border-b border-gray-100"
                            >
                              {val > 0 ? (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0085BD]/10 text-[#0085BD] font-bold text-xs">
                                  {val}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Visão Detalhada */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <MdDescription size={20} className="text-[#0085BD]" />
              Visão Detalhada
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Selecione os setores para visualizar todos os relatórios do período
              com imagens, notas e observações.
            </p>

            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setores
                </label>
                <MultiSelectDropdown
                  options={report.crossTable.rows.map((row) => ({
                    value: row.sector_id,
                    label: row.sector_name,
                  }))}
                  value={selectedSectorIds}
                  onChange={setSelectedSectorIds}
                  placeholder="Selecione os setores..."
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleLoadDetailedView}
                  disabled={selectedSectorIds.length === 0 || loadingDetails}
                  className="h-[52px] px-6 bg-[#0085BD] text-white rounded-lg font-medium hover:bg-[#006a96] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingDetails ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <MdSearch size={20} />
                      Carregar Detalhes
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Resultados da Visão Detalhada */}
            {detailedView.length > 0 && (
              <div className="space-y-6 mt-6 border-t border-gray-100 pt-6">
                {detailedView.map((reportDetail) => (
                  <div
                    key={reportDetail.report_id}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Header do Relatório */}
                    <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium opacity-90">
                            Relatório #{reportDetail.report_code}
                          </span>
                          <p className="text-xs opacity-75 mt-1">
                            {format(new Date(reportDetail.report_date), "dd 'de' MMMM 'de' yyyy", {
                              locale: ptBR,
                            })}
                          </p>
                        </div>
                        <MdAssessment size={24} className="opacity-75" />
                      </div>
                    </div>

                    {/* Setores do Relatório */}
                    <div className="p-4 space-y-4">
                      {reportDetail.sectors.map((sector) => (
                        <div
                          key={sector.sector_id}
                          className="bg-gray-50 rounded-lg p-4 space-y-3"
                        >
                          {/* Nome do Setor */}
                          <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                            <MdLocationOn className="text-[#0085BD]" size={18} />
                            <h4 className="font-semibold text-gray-800">
                              {sector.sector_name}
                            </h4>
                          </div>

                          {/* Observação */}
                          {sector.observation && (
                            <div className="bg-white rounded-lg p-3">
                              <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">
                                Observação
                              </p>
                              <p className="text-sm text-gray-700">
                                {sector.observation}
                              </p>
                            </div>
                          )}

                          {/* Equipamentos */}
                          {sector.equipments.length > 0 && (
                            <div className="bg-white rounded-lg p-3">
                              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-1">
                                <MdBuild size={14} />
                                Equipamentos
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {sector.equipments.map((eq) => (
                                  <span
                                    key={eq.id}
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${getGradeColor(
                                      eq.grade
                                    )}`}
                                  >
                                    {eq.name} — {getGradeLabel(eq.grade)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Imagens */}
                          {sector.images.length > 0 && (
                            <div className="bg-white rounded-lg p-3">
                              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-1">
                                <MdImage size={14} />
                                Imagens ({sector.images.length})
                              </p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {sector.images.map((img) => (
                                  <div
                                    key={img.id}
                                    className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
                                  >
                                    <img
                                      src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${img.file_name}`}
                                      alt="Imagem do setor"
                                      className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                                      onClick={() =>
                                        window.open(
                                          `https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${img.file_name}`,
                                          "_blank"
                                        )
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {detailedView.length === 0 && selectedSectorIds.length > 0 && !loadingDetails && (
              <p className="text-sm text-gray-400 text-center py-8">
                Clique em "Carregar Detalhes" para visualizar as informações.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  bg,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bg: string;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}
